import tooltipText from '../../../components/common/TooltipText.json'
import {
  getFilterValuesToApply,
  getSelectedUseCaseFilters,
  mergeUseCaseFilters,
  isDynamicFilter,
  getUseCasesByFilterKey,
  forEachUseCase
} from '../../../utils/useCaseFilterMapping'

const updateUseCaseIds = (subTopic, selectedUseCases) => {
  for (const useCase of subTopic.useCases) {
    if (useCase.selected) {
      selectedUseCases.push(useCase.id)
    }
  }
}

export const getSelectedUseCases = (useCasesData) => {
  const selectedUseCases = []
  for (const item of useCasesData || []) {
    for (const topic of item.topics || []) {
      for (const subTopic of topic.subTopics) {
        updateUseCaseIds(subTopic, selectedUseCases)
      }
    }
  }
  return selectedUseCases
}

export const getSelectedUseCasesCountByDomain = (domain) => {
  const selectedUseCases = []
  for (const topic of domain?.topics || []) {
    for (const subTopic of topic?.subTopics || []) {
      updateUseCaseIds(subTopic, selectedUseCases)
    }
  }
  return selectedUseCases?.length
}

export const updateSelectedData = (type, data, option, setSelectedFilters) => {
  if (type === 'Filters' || type === 'Health Conditions' || type === 'Community Factors') {
    if (Array.isArray(option)) {
      setSelectedFilters([])
    } else {
      setSelectedFilters(data.filter((entry) => entry.FILTER_ID !== option.FILTER_ID) || [])
    }
  }
}

const isUseCaseFilter = (useCase, option) => {
  if (Array.isArray(option)) {
    return option.some((filter) => useCase.filters.map((filter) => filter.name).includes(filter.title))
  } else {
    return useCase.filters.map((filter) => filter.name).includes(option.title)
  }
}

const populateSelectedUseCases = (useCase, selectedUseCases) => {
  if (!selectedUseCases.some((item) => item.id === useCase.id)) {
    selectedUseCases.push({
      id: useCase.id,
      name: useCase.name
    })
  }
}

export const handleUpdateSelectedUseCases = (topic, selectedUseCases, option) => {
  for (const subTopic of topic.subTopics) {
    for (const useCase of subTopic.useCases) {
      if (useCase.selected && isUseCaseFilter(useCase, option)) {
        populateSelectedUseCases(useCase, selectedUseCases)
      }
    }
  }
}

export const hideUseCase = (useCase) => {
  return useCase.disabled && useCase.tooltipText === tooltipText.filterDisabled
}

export const hideSubTopic = (subTopic) => {
  return subTopic?.useCases?.every((useCase) => useCase.disabled && useCase.tooltipText === tooltipText.filterDisabled)
}

export const hideTopic = (topic) => {
  return topic?.subTopics?.every((subTopic) => hideSubTopic(subTopic))
}

export const hideDomain = (domain) => {
  return domain?.topics?.every((topic) => hideTopic(topic))
}

export const isNoUseCases = (useCasesData) => {
  return useCasesData?.every((domain) => hideDomain(domain))
}

/**
 * Process a single useCase based on dashboard context and permissions
 */
const processSingleUseCase = (useCase, existingUseCases, fromDashboard, permissionDetails) => {
  const isExisting = existingUseCases?.includes(useCase?.id)

  // Admin from dashboard: enable all
  if (fromDashboard && permissionDetails?.isAdmin) {
    return {...useCase, selected: false, disabled: false, tooltipText: ''}
  }

  // Non-admin from dashboard
  if (fromDashboard) {
    return isExisting
      ? {...useCase, selected: false, disabled: false, tooltipText: ''}
      : {...useCase, selected: false, disabled: true, tooltipText: tooltipText.filterDisabled}
  }

  // Not from dashboard (admin panel)
  return isExisting ? {...useCase, selected: true, disabled: false} : {...useCase, selected: false, disabled: true}
}

const processUseCases = (useCases, existingUseCases, fromDashboard, permissionDetails) => {
  return useCases?.map((useCase) => processSingleUseCase(useCase, existingUseCases, fromDashboard, permissionDetails))
}

const processSubTopics = (subTopics, existingUseCases, fromDashboard, permissionDetails) => {
  return subTopics.map((subTopic) => ({
    ...subTopic,
    expanded: true,
    useCases: processUseCases(subTopic.useCases, existingUseCases, fromDashboard, permissionDetails)
  }))
}

const processTopics = (topics, existingUseCases, fromDashboard, permissionDetails) => {
  return topics.map((topic) => ({
    ...topic,
    subTopics: processSubTopics(topic.subTopics, existingUseCases, fromDashboard, permissionDetails)
  }))
}

export const getInitialUseCaseConfig = (
  mappings,
  existingUseCases,
  fromDashboard = false,
  permissionDetails = null
) => {
  return mappings.map((item) => ({
    ...item,
    topics: processTopics(item.topics, existingUseCases, fromDashboard, permissionDetails)
  }))
}

export const isUseCasesSelectedByDomain = (topics) => {
  return topics?.some((topic) =>
    topic.subTopics.some((subTopic) => subTopic.useCases.some((useCase) => useCase.selected))
  )
}

export const isUseCasesSelectedByTopics = (subTopics) => {
  return subTopics.some((subTopic) => subTopic.useCases.some((useCase) => useCase.selected))
}

const disableOtherUseCases = (subTopic, limit) => {
  return {
    ...subTopic,
    useCases: subTopic.useCases.map((useCase) => {
      if (!useCase.selected) {
        if (limit === 2 && useCase.tooltipText !== tooltipText.filterDisabled) {
          return {
            ...useCase,
            disabled: true,
            tooltipText: tooltipText.maxUseCaseSelection
          }
        } else if (
          useCase.tooltipText === tooltipText.maxUseCaseSelection &&
          useCase.tooltipText !== tooltipText.filterDisabled
        ) {
          return {
            ...useCase,
            disabled: false,
            tooltipText: ''
          }
        }
      }
      return useCase
    })
  }
}

export const handleUseCaseSelectionMaxLimitReached = (useCases, limit) => {
  return useCases?.map((item) => {
    return {
      ...item,
      topics: item.topics.map((topic) => {
        return {
          ...topic,
          subTopics: topic.subTopics.map((subTopic) => disableOtherUseCases(subTopic, limit))
        }
      })
    }
  })
}

// ============================================================================
// NEW FILTER MANAGEMENT SYSTEM FUNCTIONS
// These functions work with filterKey and filterManagementSlice
// ============================================================================

/**
 * Handle filter updates when a usecase is selected/deselected
 * Uses the new filterManagementSlice for unified filter management
 *
 * For dynamic filters (those with empty options that need API fetch),
 * values are stored as "pending" and applied when options load.
 *
 * @param {Array} currentUseCaseFilters - Current usecase's filters (for comparison)
 * @param {Array} useCases - The full useCases data structure
 * @param {Object} filterManagement - Object containing filter management actions from useFilterManagement hook
 * @param {Object} filterConfig - The filter configuration data
 */
export const handleUpdateFilters = (currentUseCaseFilters, useCases, filterManagement, filterConfig) => {
  const {addMultipleFilters, setMultipleFilterValues, setPendingFilterValues, removeFilter, addedFilters} =
    filterManagement

  // Get all selected filters from all selected usecases
  const allSelectedFilters = getSelectedUseCaseFilters(useCases)

  // Merge filters when multiple usecases select the same filter
  const mergedFilters = mergeUseCaseFilters(allSelectedFilters)

  // Extract just the filter keys for addMultipleFilters (it expects array of strings)
  const filterKeysToAdd = mergedFilters.map((f) => f.filterKey).filter(Boolean)

  // Get filter values to apply
  const filterValues = getFilterValuesToApply(mergedFilters, filterConfig)

  // Add all usecase filters to the drawer (respects availableFilters permission check)
  if (filterKeysToAdd.length > 0) {
    addMultipleFilters(filterKeysToAdd)
  }

  // Separate static and dynamic filter values
  // Dynamic filters need to wait for options to load before applying values
  const staticFilterValues = {}
  const dynamicFilterValues = {}

  for (const [filterKey, value] of Object.entries(filterValues)) {
    if (isDynamicFilter(filterKey, filterConfig)) {
      dynamicFilterValues[filterKey] = value
    } else {
      staticFilterValues[filterKey] = value
    }
  }

  // Set values for static filters immediately
  if (Object.keys(staticFilterValues).length > 0) {
    setMultipleFilterValues(staticFilterValues)
  }

  // Store pending values for dynamic filters (will be applied when options load)
  if (Object.keys(dynamicFilterValues).length > 0) {
    setPendingFilterValues(dynamicFilterValues)
  }

  // Remove filters that are no longer used by any selected usecase
  const currentFilterKeys = currentUseCaseFilters?.map((f) => f.filterKey) || []
  const remainingFilterKeys = new Set(mergedFilters.map((f) => f.filterKey))

  for (const filterKey of currentFilterKeys) {
    if (!remainingFilterKeys.has(filterKey) && addedFilters?.includes(filterKey)) {
      removeFilter(filterKey)
    }
  }
}

/**
 * Update usecases when filters are cleared using the new filter management system
 *
 * @param {Array} useCases - The full useCases data structure
 * @param {Array} clearedFilterKeys - Array of filterKeys that were cleared
 * @returns {Object} - { updatedUseCases, deselectedUseCaseNames }
 */
export const updateUseCaseOnClearFilters = (useCases, clearedFilterKeys) => {
  const deselectedUseCaseNames = []

  const updateUseCase = (useCase) => {
    if (!useCase.selected) return useCase

    const hasFilterCleared = useCase.filters?.some((filter) => clearedFilterKeys.includes(filter.filterKey))

    if (hasFilterCleared) {
      deselectedUseCaseNames.push(useCase.name)
      return {...useCase, selected: false}
    }

    return useCase
  }

  const updateSubTopic = (subTopic) => ({
    ...subTopic,
    useCases: subTopic.useCases?.map(updateUseCase)
  })

  const updateTopic = (topic) => ({
    ...topic,
    subTopics: topic.subTopics?.map(updateSubTopic)
  })

  const updateDomain = (domain) => ({
    ...domain,
    topics: domain.topics?.map(updateTopic)
  })

  const updatedUseCases = useCases.map(updateDomain)

  const selectedUseCaseCount = getSelectedUseCases(updatedUseCases).length
  const finalUseCases = handleUseCaseSelectionMaxLimitReached(
    updatedUseCases,
    selectedUseCaseCount === 2 ? 2 : selectedUseCaseCount
  )

  return {
    updatedUseCases: finalUseCases,
    deselectedUseCaseNames
  }
}

/**
 * Handle usecase deselection when a filter is removed using the new filter management system
 * Uses filterKey instead of filter name for matching
 *
 * @param {string} filterKey - The filterKey that was removed
 * @param {Array} useCases - The full useCases data structure
 * @param {Function} setUseCases - Function to update useCases state
 * @param {Function} setIsSnackBarOpen - Function to control snackbar visibility
 * @param {Function} setSnackbarMessage - Function to set snackbar message
 * @returns {boolean} - True if any usecases were deselected
 */
/**
 * Get names of selected usecases that have a specific filter
 */
const getSelectedUseCaseNamesWithFilter = (filterKey, useCases) => {
  const names = []
  forEachUseCase(useCases, (useCase) => {
    const hasFilter = useCase.filters?.some((f) => f.filterKey === filterKey)
    if (hasFilter && useCase.selected) {
      names.push(useCase.name)
    }
  })
  return names
}

export const handleUseCaseOnFilterRemove = (
  filterKey,
  useCases,
  setUseCases,
  setIsSnackBarOpen,
  setSnackbarMessage
) => {
  // Use the shared utility to get IDs
  const deselectUseCaseIds = getUseCasesByFilterKey(filterKey, useCases)

  if (deselectUseCaseIds.length === 0) {
    return false
  }

  // Get names for the snackbar message
  const deselectedUseCaseNames = getSelectedUseCaseNamesWithFilter(filterKey, useCases)
  const plural = deselectedUseCaseNames.length > 1

  setIsSnackBarOpen(false)
  setSnackbarMessage(
    <>
      Since this filter has been removed, the associated use case{plural ? 's' : ''}
      <b> &quot;{deselectedUseCaseNames.join(', ')}&quot; </b>
      {plural ? 'are' : 'is'} now detached
    </>
  )
  setTimeout(() => {
    setIsSnackBarOpen(true)
  }, 100)
  updateUseCasesOnFilterRemove(deselectUseCaseIds, useCases, setUseCases)

  return true
}

/**
 * Update a single useCase - deselect if in the list
 */
const updateUseCaseSelection = (useCase, deselectIds) => {
  if (deselectIds.includes(useCase.id)) {
    return {...useCase, selected: false}
  }
  return useCase
}

/**
 * Update useCases within a subTopic
 */
const updateSubTopicUseCases = (subTopic, deselectIds) => ({
  ...subTopic,
  useCases: subTopic.useCases?.map((uc) => updateUseCaseSelection(uc, deselectIds))
})

/**
 * Update subTopics within a topic
 */
const updateTopicSubTopics = (topic, deselectIds) => ({
  ...topic,
  subTopics: topic.subTopics?.map((st) => updateSubTopicUseCases(st, deselectIds))
})

/**
 * Update topics within a domain
 */
const updateDomainTopics = (domain, deselectIds) => ({
  ...domain,
  topics: domain.topics?.map((t) => updateTopicSubTopics(t, deselectIds))
})

/**
 * Update useCases state when usecases are deselected due to filter removal
 * Works with the new filterKey-based system
 *
 * @param {Array} deselectUseCaseIds - Array of usecase IDs to deselect
 * @param {Array} useCases - The full useCases data structure
 * @param {Function} setUseCases - Function to update useCases state
 */
export const updateUseCasesOnFilterRemove = (deselectUseCaseIds, useCases, setUseCases) => {
  const updatedUseCases = useCases.map((domain) => updateDomainTopics(domain, deselectUseCaseIds))

  const selectedUseCaseCount = getSelectedUseCases(updatedUseCases).length
  const finalUseCases = handleUseCaseSelectionMaxLimitReached(updatedUseCases, selectedUseCaseCount)
  setUseCases(finalUseCases)
}

/**
 * Check if a usecase filter matches a filterKey
 * Helper function for the new filterKey-based system
 *
 * @param {Object} useCase - The usecase object
 * @param {string} filterKey - The filterKey to check
 * @returns {boolean} - True if the usecase has this filter
 */
export const isUseCaseFilterByKey = (useCase, filterKey) => {
  return useCase.filters?.some((f) => f.filterKey === filterKey)
}

/**
 * Get all filterKeys from a usecase's filters
 *
 * @param {Object} useCase - The usecase object
 * @returns {Array} - Array of filterKeys
 */
export const getUseCaseFilterKeys = (useCase) => {
  return useCase.filters?.map((f) => f.filterKey) || []
}
